import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"
import {
  apexChartGridPadding,
  apexTooltipOptions,
  apexDataLabelPill,
  observeChartTheme,
  observeCompactChartViewport,
  readChartTheme
} from "../utils/chart_theme"
import { hideCrosshair, syncCrosshairToMarker } from "../utils/chart_geometry"
import {
  fillMultiFlowMonthBuckets,
  monthRangeEndingAt,
  monthRangeForYear,
  monthRangeFromFirstDataToToday
} from "../utils/chart_timeline_aggregation"
import { buildChartIndexFilterUrl, visitChartIndexFilterUrl } from "../utils/chart_index_filter_url"
import {
  clearTooltipPositioned,
  positionApexTooltip
} from "../utils/apex_tooltip_position"

const MONTH_SHORT_FR = [
  "janv.", "févr.", "mars", "avr.", "mai", "juin",
  "juil.", "août", "sept.", "oct.", "nov.", "déc."
]

const MONTH_FULL_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
]

// Vertical layout (bottom → top): bars ~3–50% | gap 50–62% | line 62–88%
// ZERO at 0.25 lands on a grid tick when tickAmount = 5 (bar axis).
const ZERO_LINE_FRACTION = 0.25
const POS_BAR_BAND = 0.22
const NEG_BAR_BAND = 0.22
const LINE_BAND_START = 0.62
const LINE_BAND_END = 0.88
const BAR_SCALE_PERCENTILE = 95
// Clipped flows: tallest clip reaches cap + headroom; others scale by surplus ratio.
const CLIP_VISUAL_HEADROOM = 0.62
const CHART_HEIGHT = 520
// Pos labels sit above the bar top; neg labels below the bar bottom (Apex needs extra baseline on neg).
const CLIP_LABEL_BAR_GAP = 10
const CLIP_LABEL_POS_OFFSET = -CLIP_LABEL_BAR_GAP
const CLIP_LABEL_NEG_OFFSET = CLIP_LABEL_BAR_GAP + 22
const CLIP_LABEL_NEG_OFFSET_CLIPPED = CLIP_LABEL_NEG_OFFSET + 18
const ANNOTATION_LABEL_STEP = 20
const TIMELINE_MONTHLY_MAX = 18
const TIMELINE_TARGET_MIN = 10
const TIMELINE_TARGET_MAX = 20

const BAR_SERIES = {
  subscriptions: 1,
  imports: 2,
  terminations: 3
}

const DRILLDOWN_SERIES_KEY = {
  [BAR_SERIES.subscriptions]: "subscriptions",
  [BAR_SERIES.imports]: "imports",
  [BAR_SERIES.terminations]: "terminations"
}

const FLOW_SERIES_TO_BAR_INDEX = {
  subscriptions: BAR_SERIES.subscriptions,
  imports: BAR_SERIES.imports,
  terminations: BAR_SERIES.terminations
}

export default class extends Controller {
  static targets = ["chart", "rangeButton", "flowLegendItem"]
  static values = {
    years: Object,
    selectedRange: { type: String, default: "12m" }
  }

  connect() {
    this.activeFlowSeries = null
    this.activeDataPointIndex = null
    this.highlightSource = null
    this.hoveredBarPath = null
    this.unobserveTheme = observeChartTheme(() => this.renderChart())
    this.unobserveCompactViewport = observeCompactChartViewport(() => this.renderChart())
    this.boundComponentPointerLeave = (event) => this.handleComponentPointerLeave(event)
    this.element.addEventListener("mouseleave", this.boundComponentPointerLeave)
    this.rafId = requestAnimationFrame(() => {
      this.rafId = requestAnimationFrame(() => this.renderChart())
    })
  }

  disconnect() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.clearFlowHighlight()
    this.unobserveTheme?.()
    this.unobserveCompactViewport?.()
    this.element.removeEventListener("mouseleave", this.boundComponentPointerLeave)
    this.destroyChart()
  }

  selectRange(event) {
    event.preventDefault()
    event.stopPropagation()

    const range = event.currentTarget.dataset.range
    if (!range || range === this.selectedRangeValue) return

    const scrollY = window.scrollY
    this.selectedRangeValue = range
    this.syncRangeButtons()
    this.updateChart(this.buildOptions()).finally(() => this.restoreScroll(scrollY))
  }

  restoreScroll(scrollY) {
    window.scrollTo({ top: scrollY, left: 0, behavior: "instant" })
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, left: 0, behavior: "instant" })
    })
  }

  selectedRangeValueChanged() {
    if (!this.chartInstance) return
    this.syncRangeButtons()
  }

  renderChart() {
    if (!this.hasChartTarget) return

    try {
      this.updateChart(this.buildOptions())
      this.syncRangeButtons()
    } catch (error) {
      console.error("[monthly-contracts-chart] render failed", error)
    }
  }

  updateChart(options) {
    if (this.chartInstance) {
      return this.chartInstance.updateOptions(options, false, true)
    }

    // Premier montage seulement — le chemin `updateOptions` ci-dessus garde le graphique en
    // place. ApexCharts empile ses `.apexcharts-canvas` sans jamais vider son conteneur, et
    // le corps du tableau de bord est greffé d'une visite à l'autre : le graphique hérité de
    // la visite précédente n'appartient plus à personne, il faut le balayer explicitement.
    this.chartTarget.replaceChildren()
    this.chartInstance = new ApexCharts(this.chartTarget, options)
    // Pas de ResizeObserver maison ici : ApexCharts surveille déjà la fenêtre et la taille du
    // parent, et se remesure tout seul.
    return this.chartInstance.render()
  }

  destroyChart() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
      this.chartInstance = null
    }
  }

  handleBarClick(config) {
    const normalized = this.normalizeApexPointConfig(config)
    const seriesIndex = normalized?.seriesIndex
    const dataPointIndex = normalized?.dataPointIndex
    const seriesKey = DRILLDOWN_SERIES_KEY[seriesIndex]
    if (!seriesKey || dataPointIndex == null) return
    if (!this.yearsValue?.drilldown) return
    if (!this.barSegmentHasValue(seriesIndex, dataPointIndex)) return

    visitChartIndexFilterUrl(this.filterUrlForBarPoint(seriesKey, dataPointIndex))
  }

  normalizeApexPointConfig(config) {
    if (!config) return null
    const nested = config.config ?? config
    const seriesIndex = nested.seriesIndex ?? config.seriesIndex
    const dataPointIndex = nested.dataPointIndex ?? config.dataPointIndex
    if (seriesIndex == null || dataPointIndex == null) return null
    return { seriesIndex, dataPointIndex }
  }

  setupBarInteractions(chart) {
    const root = chart?.el
    if (!root || root.dataset.monthlyContractsBarBound === "true") return

    root.dataset.monthlyContractsBarBound = "true"
    root.addEventListener("click", (event) => this.handleBarPointerEvent(event))
    root.addEventListener("mousemove", (event) => this.handleBarHoverEvent(event))
    root.addEventListener("mouseover", (event) => this.handleTooltipRowHover(event))
  }

  handleTooltipRowHover(event) {
    const row = event.target?.closest?.("[data-flow-series]")
    if (!row || this.activeDataPointIndex == null) return

    this.setFlowHighlight({
      flowSeries: row.dataset.flowSeries,
      dataPointIndex: this.activeDataPointIndex,
      source: "legend"
    })
  }

  flowLegendEnter(event) {
    const flowSeries = event.currentTarget.dataset.flowSeries
    if (!flowSeries) return

    this.setFlowHighlight({
      flowSeries,
      dataPointIndex: this.activeDataPointIndex,
      source: "legend"
    })
  }

  flowLegendLeave(event) {
    const related = event.relatedTarget
    if (related && (this.chartTarget?.contains(related) || this.element.contains(related))) return

    this.clearFlowHighlight()
  }

  flowLegendClick(event) {
    event.preventDefault()
    this.drilldownFlowSeries(event.currentTarget.dataset.flowSeries)
  }

  drilldownFlowSeries(flowSeries) {
    if (!flowSeries || !this.yearsValue?.drilldown) return
    if (this.activeDataPointIndex == null) return

    const seriesIndex = FLOW_SERIES_TO_BAR_INDEX[flowSeries]
    if (seriesIndex == null) return
    if (!this.barSegmentHasValue(seriesIndex, this.activeDataPointIndex)) return

    visitChartIndexFilterUrl(this.filterUrlForBarPoint(flowSeries, this.activeDataPointIndex))
  }

  handleComponentPointerLeave(event) {
    if (event.relatedTarget && this.element.contains(event.relatedTarget)) return

    this.activeDataPointIndex = null
    this.syncLegendDrilldownState()
    clearTooltipPositioned(this.chartInstance?.chart ?? this.chartInstance)
    this.clearFlowHighlight()
  }

  setFlowHighlight({ flowSeries, dataPointIndex, source }) {
    this.activeFlowSeries = flowSeries
    if (dataPointIndex != null) this.activeDataPointIndex = dataPointIndex
    this.highlightSource = source
    this.applyHighlightVisuals()
    this.syncTooltipRowHighlight()
  }

  clearFlowHighlight() {
    this.activeFlowSeries = null
    this.highlightSource = null
    this.clearBarHoverPaths()
    this.applyHighlightVisuals()
    this.syncTooltipRowHighlight()
  }

  applyHighlightVisuals() {
    if (this.hasFlowLegendItemTarget) {
      this.flowLegendItemTargets.forEach((element) => {
        const key = element.dataset.flowSeries
        const active = this.activeFlowSeries && key === this.activeFlowSeries
        const dimmed = this.activeFlowSeries && key !== this.activeFlowSeries

        element.classList.toggle("monthly-contracts-chart__legend-item--active", active)
        element.classList.toggle("monthly-contracts-chart__legend-item--dimmed", dimmed)
      })
    }

    const root = this.chartInstance?.el
    if (!root) return

    root.querySelectorAll(".apexcharts-bar-series path").forEach((path) => {
      path.classList.remove("monthly-contracts-chart__bar-hovered", "monthly-contracts-chart__bar-dimmed")
    })

    if (!this.activeFlowSeries) return

    const seriesIndex = FLOW_SERIES_TO_BAR_INDEX[this.activeFlowSeries]
    if (seriesIndex == null) return

    const seriesPaths = root.querySelectorAll(
      `.apexcharts-series[rel="${seriesIndex}"] .apexcharts-bar-series path`
    )

    if (this.highlightSource === "legend") {
      const monthIndex = this.activeDataPointIndex
      const focusedPath =
        monthIndex != null ? this.barPathForPoint(seriesIndex, monthIndex) : null

      if (focusedPath) {
        focusedPath.classList.add("monthly-contracts-chart__bar-hovered")
        root.querySelectorAll(".apexcharts-bar-series path").forEach((path) => {
          if (path !== focusedPath) path.classList.add("monthly-contracts-chart__bar-dimmed")
        })
      } else {
        seriesPaths.forEach((path) => path.classList.add("monthly-contracts-chart__bar-hovered"))
        root.querySelectorAll(".apexcharts-bar-series path").forEach((path) => {
          const rel = Number(path.closest(".apexcharts-series")?.getAttribute("rel"))
          if (rel !== seriesIndex) path.classList.add("monthly-contracts-chart__bar-dimmed")
        })
      }
      return
    }

    if (this.hoveredBarPath) {
      root.querySelectorAll(".apexcharts-bar-series path").forEach((path) => {
        if (path === this.hoveredBarPath) {
          path.classList.add("monthly-contracts-chart__bar-hovered")
        } else {
          path.classList.add("monthly-contracts-chart__bar-dimmed")
        }
      })
    }
  }

  positionTooltipNearColumn(chart, dataPointIndex) {
    const instance = chart?.chart ?? chart ?? this.chartInstance
    positionApexTooltip(instance, dataPointIndex, {
      mode: "composite",
      offsetX: 10,
      gap: 8,
      barZoneTopFraction: 0.44
    })
  }

  syncLegendDrilldownState() {
    const ready =
      this.yearsValue?.drilldown && this.activeDataPointIndex != null
    this.element.classList.toggle("monthly-contracts-chart--month-hovered", ready)

    if (!this.hasFlowLegendItemTarget) return

    this.flowLegendItemTargets.forEach((element) => {
      element.classList.toggle("monthly-contracts-chart__legend-item--month-ready", ready)
    })
  }

  barPathForPoint(seriesIndex, dataPointIndex) {
    const root = this.chartInstance?.el
    if (!root) return null

    const paths = root.querySelectorAll(
      `.apexcharts-series[rel="${seriesIndex}"] .apexcharts-bar-series path`
    )
    return paths[dataPointIndex] || null
  }

  syncTooltipRowHighlight() {
    const tooltip = this.chartTarget?.querySelector(".apexcharts-tooltip")
    if (!tooltip) return

    tooltip.querySelectorAll("[data-flow-series]").forEach((row) => {
      const key = row.dataset.flowSeries
      row.classList.toggle("monthly-contracts-chart__tooltip-row--active", key === this.activeFlowSeries)
      row.classList.toggle(
        "monthly-contracts-chart__tooltip-row--dimmed",
        this.activeFlowSeries && key !== this.activeFlowSeries
      )
    })
  }

  clearBarHoverPaths() {
    this.hoveredBarPath = null
    this.chartInstance?.el
      ?.querySelectorAll(".apexcharts-bar-series path")
      ?.forEach((path) => {
        path.classList.remove("monthly-contracts-chart__bar-hovered", "monthly-contracts-chart__bar-dimmed")
      })
  }

  handleBarPointerEvent(event) {
    const flowRow = event.target?.closest?.("[data-flow-series]")
    if (flowRow) {
      event.preventDefault()
      this.drilldownFlowSeries(flowRow.dataset.flowSeries)
      return
    }

    const meta = this.barPointerMeta(event)
    if (!meta) return

    const seriesKey = DRILLDOWN_SERIES_KEY[meta.seriesIndex]
    if (!seriesKey) return
    if (!this.yearsValue?.drilldown) return
    if (!this.barSegmentHasValue(meta.seriesIndex, meta.dataPointIndex)) return

    visitChartIndexFilterUrl(this.filterUrlForBarPoint(seriesKey, meta.dataPointIndex))
  }

  barPointerMeta(event) {
    const path = event.target?.closest?.(".apexcharts-bar-series path")
    if (!path) return null

    const seriesRoot = path.closest(".apexcharts-series")
    if (!seriesRoot) return null

    const seriesIndex = Number(seriesRoot.getAttribute("rel"))
    const dataPointIndex = Number(path.getAttribute("j") ?? path.getAttribute("index"))
    if (!Number.isFinite(seriesIndex) || !Number.isFinite(dataPointIndex)) return null

    return { seriesIndex, dataPointIndex }
  }

  handleBarHoverEvent(event) {
    const meta = this.barPointerMeta(event)
    this.hoveredBarPath = meta ? event.target.closest(".apexcharts-bar-series path") : null

    if (!meta) {
      if (this.highlightSource === "chart") this.clearFlowHighlight()
      return
    }

    const flowSeries = DRILLDOWN_SERIES_KEY[meta.seriesIndex]
    if (!flowSeries) return

    this.setFlowHighlight({
      flowSeries,
      dataPointIndex: meta.dataPointIndex,
      source: "chart"
    })
  }

  barSegmentHasValue(seriesIndex, dataPointIndex) {
    const raw = this.viewContext?.rawSeries?.[seriesIndex]?.data?.[dataPointIndex]
    if (raw == null || Number.isNaN(raw)) return false
    return Math.abs(Number(raw)) > 0
  }

  filterUrlForBarPoint(seriesKey, dataPointIndex) {
    const drilldown = this.yearsValue.drilldown
    const seriesConfig = drilldown?.series?.[seriesKey]
    const meta = this.viewContext?.monthMeta?.[dataPointIndex]
    if (!seriesConfig || !meta || !drilldown?.indexPath) return null

    const config = {
      attribute: seriesConfig.attribute,
      scopeFilters: seriesConfig.scopeFilters,
      dateScopeSql: seriesConfig.dateScopeSql,
      indexPath: drilldown.indexPath,
      contextualAuthorizedGroups: drilldown.contextualAuthorizedGroups
    }
    const granularity = meta.granularity || this.viewContext?.granularity || "month"
    return buildChartIndexFilterUrl(config, meta, granularity)
  }

  syncRangeButtons() {
    if (!this.hasRangeButtonTarget) return

    this.rangeButtonTargets.forEach((button) => {
      const active = button.dataset.range === this.selectedRangeValue
      button.classList.toggle("depozen-ui-filter-pill--active", active)
      button.classList.toggle("bg-primary", active)
      button.classList.toggle("text-white", active)
      button.classList.toggle("depozen-ui-filter-pill--inactive", !active)
      button.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  buildOptions() {
    const theme = readChartTheme()
    const colors = this.yearsValue.colors || {}
    const active = colors.active || "#198754"
    const view = this.buildView()
    this.viewContext = view
    const scales = this.computeScales(view.caps, view.rawSeries)
    const dataLabelPill = apexDataLabelPill(active, theme)
    const series = view.series
    const palette = [active, colors.subscriptions, colors.imports, colors.terminations]
    const manyPoints = view.categories.length > 18

    return {
      chart: {
        type: "line",
        height: CHART_HEIGHT,
        width: "100%",
        fontFamily: "var(--headings-font, 'Space Grotesk', BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif)",
        toolbar: { show: false },
        zoom: manyPoints ? { enabled: true, type: "x", allowMouseWheelZoom: false } : { enabled: false },
        selection: { enabled: false },
        animations: {
          enabled: true,
          speed: 450,
          animateGradually: { enabled: true, delay: 80 }
        },
        parentHeightOffset: 15,
        stacked: true,
        events: {
          mounted: (ctx) => {
            const chart = ctx?.chart ?? ctx
            hideCrosshair(chart)
            this.setupBarInteractions(chart)
          },
          updated: (ctx) => {
            const chart = ctx?.chart ?? ctx
            hideCrosshair(chart)
            this.setupBarInteractions(chart)
          },
          animationEnd: (ctx) => hideCrosshair(ctx?.chart ?? ctx),
          mouseMove: (_event, ctx, config) => {
            const chart = ctx?.chart ?? ctx
            const dataPointIndex = config?.dataPointIndex
            if (dataPointIndex != null) this.activeDataPointIndex = dataPointIndex
            this.syncLegendDrilldownState()
            syncCrosshairToMarker(chart, dataPointIndex)
            this.positionTooltipNearColumn(chart, dataPointIndex)
            this.syncTooltipRowHighlight()
          },
          mouseLeave: (event, ctx) => {
            if (event?.relatedTarget && this.element.contains(event.relatedTarget)) return

            const chart = ctx?.chart ?? ctx
            hideCrosshair(chart)
            clearTooltipPositioned(chart?.chart ?? chart ?? this.chartInstance)
            this.clearFlowHighlight()
          },
          click: (_event, _ctx, config) => this.handleBarClick(config)
        }
      },
      series,
      colors: palette,
      stroke: {
        show: true,
        width: [3, 1, 1, 1],
        curve: ["monotoneCubic", "straight", "straight", "straight"],
        colors: [active, theme.bodyBg, theme.bodyBg, theme.bodyBg]
      },
      fill: {
        type: ["gradient", "solid", "solid", "solid"],
        opacity: [0.22, 1, 1, 1],
        gradient: {
          type: "vertical",
          shadeIntensity: 0.15,
          opacityFrom: 0.35,
          opacityTo: 0.02,
          stops: [0, 28, 52]
        }
      },
      plotOptions: {
        bar: {
          columnWidth: manyPoints ? "68%" : "58%",
          borderRadius: 4,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
          dataLabels: {
            position: "top"
          }
        }
      },
      dataLabels: {
        enabled: !manyPoints,
        enabledOnSeries: [0],
        hideOverflowingLabels: true,
        hideOverlappingLabels: true,
        offsetY: -24,
        offsetX: 0,
        textAnchor: "middle",
        style: {
          fontSize: "11px",
          fontWeight: 700,
          colors: [dataLabelPill.textColor],
          fontFamily: "var(--headings-font, 'Space Grotesk', BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif)"
        },
        background: dataLabelPill.background,
        formatter: (value) => {
          if (value == null || Number.isNaN(value)) return ""
          return this.formatChartNumber(value, { compact: true })
        }
      },
      markers: {
        size: [manyPoints ? 3 : 5, 0, 0, 0],
        colors: [active],
        strokeColors: [theme.bodyBg],
        strokeWidth: 2,
        hover: { size: 6, sizeOffset: 0 }
      },
      grid: {
        show: true,
        borderColor: theme.gridColor,
        strokeDashArray: 0,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: apexChartGridPadding(
          { top: 80, right: 0, bottom: this.bottomGridPadding(view), left: 16 },
          {
            top: 62,
            right: 0,
            bottom: Math.max(16, this.bottomGridPadding(view) - 8),
            left: 4
          }
        )
      },
      xaxis: {
        categories: view.categories,
        tickPlacement: "on",
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
        crosshairs: {
          stroke: { color: theme.gridColor, dashArray: 4 }
        },
        labels: {
          rotate: manyPoints ? -45 : 0,
          rotateAlways: manyPoints,
          hideOverlappingLabels: true,
          minHeight: 24,
          offsetY: 0,
          style: {
            colors: theme.axisLabelColor,
            fontSize: manyPoints ? "10px" : "12px",
            fontWeight: 500
          }
        }
      },
      yaxis: this.buildYAxis(scales),
      annotations: this.buildAnnotations(view, colors, scales.bars),
      legend: { show: false },
      tooltip: {
        ...apexTooltipOptions(theme),
        shared: true,
        intersect: false,
        followCursor: false,
        hideEmptySeries: false,
        cssClass: "monthly-contracts-chart__apex-tooltip",
        custom: ({ dataPointIndex }) => {
          if (dataPointIndex != null) this.activeDataPointIndex = dataPointIndex
          queueMicrotask(() => {
            this.positionTooltipNearColumn(this.chartInstance, dataPointIndex)
            this.syncTooltipRowHighlight()
          })
          return this.buildTooltipHtml(dataPointIndex)
        }
      },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } }
      }
    }
  }

  // Bars in lower band (positive up, terminations negative down), line in upper band.
  computeScales(caps, rawSeries) {
    return {
      bars: this.bandAxisScale(caps),
      active: this.activeAxisScale(rawSeries?.[0]?.data)
    }
  }

  seriesValues(series, indexes) {
    return (series || [])
      .filter((_, index) => indexes.includes(index))
      .flatMap((item) => item.data || [])
      .filter((value) => value != null && !Number.isNaN(value))
  }

  bandAxisScale(caps = {}) {
    const maxPos = Math.max(caps.pos || caps.unified || 1, 1)
    const maxNeg = Math.max(caps.neg || 0, 0) > 0 ? Math.max(caps.unified || 1, 1) : 0
    const bottomBreathingRoom = 0.04

    if (maxNeg === 0) {
      return {
        min: 0,
        max: maxPos / POS_BAR_BAND,
        zeroFraction: 0,
        posBand: POS_BAR_BAND,
        negBand: 0
      }
    }

    const range = Math.max(maxPos / POS_BAR_BAND, maxNeg / NEG_BAR_BAND)
    const totalRange = range / (1 - bottomBreathingRoom)
    const min = -ZERO_LINE_FRACTION * totalRange
    const max = min + totalRange

    return {
      min,
      max,
      zeroFraction: ZERO_LINE_FRACTION,
      posBand: POS_BAR_BAND,
      negBand: NEG_BAR_BAND,
      cap: caps.unified || maxPos
    }
  }

  bottomGridPadding(view) {
    const clipMeta = view.clipMeta || new Map()
    const hasNegClips = [...clipMeta.values()].some((clip) => clip.kind === "neg")
    if (hasNegClips) return 72

    const hasNegBars = (view.rawSeries?.[BAR_SERIES.terminations]?.data || []).some(
      (value) => value != null && !Number.isNaN(value) && Number(value) < 0
    )
    return hasNegBars ? 32 : 24
  }

  barValueFraction(value, barScale) {
    const range = barScale.max - barScale.min
    if (range <= 0) return 0
    return (value - barScale.min) / range
  }

  barAxisLabelVisible(value, barScale) {
    if (value == null || value === 0) return false

    const zero = barScale.zeroFraction ?? this.barValueFraction(0, barScale)
    const top = zero + (barScale.posBand ?? POS_BAR_BAND) + 0.03
    const bottom = zero - (barScale.negBand ?? NEG_BAR_BAND) - 0.03
    const fraction = this.barValueFraction(value, barScale)

    return fraction >= bottom && fraction <= top
  }

  robustCap(values, percentile = BAR_SCALE_PERCENTILE) {
    const nums = (values || []).filter((value) => value != null && !Number.isNaN(value) && value > 0)
    if (!nums.length) return 1

    const sorted = [...nums].sort((a, b) => a - b)
    if (sorted.length === 1) return Math.max(sorted[0], 1)

    const rank = Math.ceil((percentile / 100) * sorted.length) - 1
    const index = Math.min(Math.max(rank, 0), sorted.length - 1)
    return Math.max(sorted[index], 1)
  }

  clipDisplayMagnitude(rawAbs, unifiedCap, maxSurplus) {
    const abs = Math.abs(Number(rawAbs))
    const cap = Math.max(unifiedCap, 1)
    if (abs <= cap) return abs

    const surplus = abs - cap
    const surplusCeiling = Math.max(maxSurplus, surplus, 1)
    const headroom = cap * CLIP_VISUAL_HEADROOM
    const bonus = (surplus / surplusCeiling) * headroom

    return cap + bonus
  }

  clippedSurplusCeilings(rawSeries, unifiedCap) {
    let maxPosSurplus = 0
    let maxNegSurplus = 0

    rawSeries.forEach((item, seriesIndex) => {
      if (seriesIndex === 0) return

      item.data.forEach((value) => {
        if (value == null || Number.isNaN(value)) return

        const abs = Math.abs(Number(value))
        if (abs <= unifiedCap) return

        const surplus = abs - unifiedCap
        if (seriesIndex === BAR_SERIES.terminations) {
          maxNegSurplus = Math.max(maxNegSurplus, surplus)
        } else {
          maxPosSurplus = Math.max(maxPosSurplus, surplus)
        }
      })
    })

    return { maxPosSurplus, maxNegSurplus }
  }

  applyBarCaps(rawSeries) {
    const barIndexes = [BAR_SERIES.subscriptions, BAR_SERIES.imports, BAR_SERIES.terminations]
    const barValues = this.seriesValues(rawSeries, barIndexes)
    const magnitudes = barValues
      .filter((value) => value != null && !Number.isNaN(value))
      .map((value) => Math.abs(Number(value)))
    const unifiedCap = magnitudes.length ? this.robustCap(magnitudes) : 1
    const hasNegative = barValues.some((value) => value != null && Number(value) < 0)
    const { maxPosSurplus, maxNegSurplus } = this.clippedSurplusCeilings(rawSeries, unifiedCap)
    const clipMeta = new Map()
    let maxNegDisplay = unifiedCap

    const series = rawSeries.map((item, seriesIndex) => {
      if (seriesIndex === 0) return { ...item, data: [...item.data] }

      return {
        ...item,
        data: item.data.map((value, dataIndex) => {
          if (value == null || Number.isNaN(value)) return null

          if (seriesIndex === BAR_SERIES.terminations) {
            const abs = Math.abs(Number(value))
            if (abs > unifiedCap) {
              const display = this.clipDisplayMagnitude(abs, unifiedCap, maxNegSurplus)
              maxNegDisplay = Math.max(maxNegDisplay, display)
              clipMeta.set(this.clipKey(seriesIndex, dataIndex), {
                raw: abs,
                cap: unifiedCap,
                display,
                kind: "neg"
              })
              return -display
            }
            maxNegDisplay = Math.max(maxNegDisplay, abs)
            return -abs
          }

          const num = Number(value)
          if (num <= 0) return null

          if (num > unifiedCap) {
            const display = this.clipDisplayMagnitude(num, unifiedCap, maxPosSurplus)
            clipMeta.set(this.clipKey(seriesIndex, dataIndex), {
              raw: num,
              cap: unifiedCap,
              display,
              kind: "pos"
            })
            return display
          }
          return num
        })
      }
    })

    let maxPosStack = unifiedCap
    const pointCount = series[0]?.data?.length || 0
    for (let dataIndex = 0; dataIndex < pointCount; dataIndex += 1) {
      const sub = series[BAR_SERIES.subscriptions]?.data?.[dataIndex]
      const imports = series[BAR_SERIES.imports]?.data?.[dataIndex]
      const stackHeight = Math.max(sub || 0, 0) + Math.max(imports || 0, 0)
      maxPosStack = Math.max(maxPosStack, stackHeight)
    }

    return {
      series,
      rawSeries,
      clipMeta,
      caps: {
        unified: unifiedCap,
        pos: maxPosStack,
        neg: hasNegative ? maxNegDisplay : 0
      },
      hasClipped: clipMeta.size > 0
    }
  }

  clipKey(seriesIndex, dataIndex) {
    return `${seriesIndex}-${dataIndex}`
  }

  chartLabels() {
    return this.yearsValue.labels || {}
  }

  stockLabel() {
    return this.chartLabels().stock || "Contrats en vigueur"
  }

  formatChartNumber(value, { compact = false } = {}) {
    if (value == null || Number.isNaN(value)) return ""
    const number = Number(value)
    if (!Number.isFinite(number)) return ""

    if (compact && Math.abs(number) >= 1000) {
      const abs = Math.abs(number)
      const scaled = number / 1000
      const fractionDigits = abs >= 1000 && abs < 100_000 ? 1 : 0
      return `${scaled.toLocaleString("fr-FR", { maximumFractionDigits: fractionDigits })}k`
    }

    return number.toLocaleString("fr-FR")
  }

  flowLabel(key, fallback) {
    return this.chartLabels()[key] || fallback
  }

  formatTooltipTitle(dataPointIndex) {
    const meta = this.viewContext?.monthMeta?.[dataPointIndex]
    if (!meta) return this.viewContext?.categories?.[dataPointIndex] || ""

    if (meta.granularity === "year") return String(meta.year)

    if (meta.granularity === "semester" && meta.semester) {
      return `S${meta.semester} ${meta.year}`
    }

    if (meta.granularity === "quarter" && meta.quarter) {
      return `T${meta.quarter} ${meta.year}`
    }

    return `${MONTH_FULL_FR[meta.monthIndex] || String(meta.monthIndex + 1)} ${meta.year}`
  }

  buildTooltipHtml(dataPointIndex) {
    const view = this.viewContext
    if (!view?.rawSeries) return ""

    const colors = this.yearsValue.colors || {}
    const clipMeta = view.clipMeta || new Map()
    const rows = [
      {
        label: this.stockLabel(),
        value: view.rawSeries[0]?.data?.[dataPointIndex],
        color: colors.active || "#198754",
        markerClass: "monthly-contracts-chart__tooltip-marker--line"
      },
      {
        seriesKey: "imports",
        label: this.flowLabel("imports", "Imports"),
        value: view.rawSeries[BAR_SERIES.imports]?.data?.[dataPointIndex],
        color: colors.imports || "#c9b836",
        markerClass: "monthly-contracts-chart__tooltip-marker--bar",
        clipKey: this.clipKey(BAR_SERIES.imports, dataPointIndex),
        clipMeta
      },
      {
        seriesKey: "subscriptions",
        label: this.flowLabel("subscriptions", "Souscriptions"),
        value: view.rawSeries[BAR_SERIES.subscriptions]?.data?.[dataPointIndex],
        color: colors.subscriptions,
        markerClass: "monthly-contracts-chart__tooltip-marker--bar",
        clipKey: this.clipKey(BAR_SERIES.subscriptions, dataPointIndex),
        clipMeta
      },
      {
        seriesKey: "terminations",
        label: this.flowLabel("terminations", "Résiliations"),
        value: view.rawSeries[BAR_SERIES.terminations]?.data?.[dataPointIndex],
        color: colors.terminations,
        markerClass: "monthly-contracts-chart__tooltip-marker--bar",
        absolute: true,
        clipKey: this.clipKey(BAR_SERIES.terminations, dataPointIndex),
        clipMeta
      }
    ]

    const body = rows
      .filter((row) => row.value != null && !Number.isNaN(row.value))
      .map((row) => {
        const amount = row.absolute ? Math.abs(Number(row.value)) : Number(row.value)
        const formatted = amount.toLocaleString("fr-FR")
        const marker = `<span class="apexcharts-tooltip-marker ${row.markerClass}" style="background-color: ${row.color};"></span>`

        const flowAttr = row.seriesKey ? ` data-flow-series="${row.seriesKey}"` : ""
        const activeClass =
          row.seriesKey && row.seriesKey === this.activeFlowSeries
            ? " monthly-contracts-chart__tooltip-row--active"
            : ""
        const dimClass =
          row.seriesKey && this.activeFlowSeries && row.seriesKey !== this.activeFlowSeries
            ? " monthly-contracts-chart__tooltip-row--dimmed"
            : ""

        const rowRole = row.seriesKey ? ' role="button" tabindex="-1"' : ""

        return `<div class="apexcharts-tooltip-series-group monthly-contracts-chart__tooltip-row${activeClass}${dimClass}"${flowAttr}${rowRole} style="display: flex; align-items: center;">
          ${marker}
          <div class="apexcharts-tooltip-text">
            <span class="apexcharts-tooltip-text-y-label">${row.label}:</span>
            <span class="apexcharts-tooltip-text-y-value"><strong>${formatted}</strong></span>
          </div>
        </div>`
      })
      .join("")

    if (!body) return ""

    const title = this.formatTooltipTitle(dataPointIndex)

    return `<div class="apexcharts-tooltip-title">${title}</div><div class="monthly-contracts-chart__tooltip-body">${body}</div>`
  }

  buildAnnotations(view, colors, _barScale = {}) {
    const clipMeta = view.clipMeta || new Map()
    const rawSeries = view.rawSeries || []
    const series = view.series || []
    const points = []

    view.categories.forEach((category, dataIndex) => {
      const subRaw = rawSeries[BAR_SERIES.subscriptions]?.data?.[dataIndex]
      const importRaw = rawSeries[BAR_SERIES.imports]?.data?.[dataIndex]
      const termRaw = rawSeries[BAR_SERIES.terminations]?.data?.[dataIndex]
      const subCapped = series[BAR_SERIES.subscriptions]?.data?.[dataIndex]
      const importCapped = series[BAR_SERIES.imports]?.data?.[dataIndex]
      const stackTop = Math.max(subCapped || 0, 0) + Math.max(importCapped || 0, 0)
      const positiveLabels = []

      if (subRaw != null && !Number.isNaN(subRaw) && Number(subRaw) > 0) {
        positiveLabels.push(this.flowLabelPayload(
          BAR_SERIES.subscriptions,
          dataIndex,
          subRaw,
          colors.subscriptions,
          clipMeta
        ))
      }

      if (importRaw != null && !Number.isNaN(importRaw) && Number(importRaw) > 0) {
        positiveLabels.push(this.flowLabelPayload(
          BAR_SERIES.imports,
          dataIndex,
          importRaw,
          colors.imports || "#c9b836",
          clipMeta
        ))
      }

      positiveLabels.forEach((label, index) => {
        if (stackTop <= 0) return

        points.push(this.flowAnnotationPoint(category, {
          text: label.text,
          y: stackTop,
          color: label.color,
          offsetY: CLIP_LABEL_POS_OFFSET - index * ANNOTATION_LABEL_STEP
        }))
      })

      if (termRaw == null || Number.isNaN(termRaw) || Number(termRaw) === 0) return

      const termCapped = series[BAR_SERIES.terminations]?.data?.[dataIndex]
      if (termCapped == null || Number.isNaN(termCapped)) return

      const termLabel = this.flowLabelPayload(
        BAR_SERIES.terminations,
        dataIndex,
        Math.abs(Number(termRaw)),
        colors.terminations,
        clipMeta,
        { negative: true }
      )
      const termClip = clipMeta.get(this.clipKey(BAR_SERIES.terminations, dataIndex))

      points.push(this.flowAnnotationPoint(category, {
        text: termLabel.text,
        y: termCapped,
        color: termLabel.color,
        offsetY: termClip ? CLIP_LABEL_NEG_OFFSET_CLIPPED : CLIP_LABEL_NEG_OFFSET
      }))
    })

    return {
      position: "front",
      points
    }
  }

  flowLabelPayload(seriesIndex, dataIndex, rawValue, color, clipMeta, { negative = false } = {}) {
    const clip = clipMeta.get(this.clipKey(seriesIndex, dataIndex))
    const formatted = this.formatChartNumber(clip?.raw ?? rawValue, { compact: true })

    if (clip) {
      return {
        text: `${formatted} ${negative ? "▼" : "▲"}`,
        color
      }
    }

    return { text: formatted, color }
  }

  flowAnnotationPoint(category, { text, y, color, offsetY }) {
    return {
      x: category,
      y,
      yAxisIndex: 1,
      marker: {
        size: 0,
        strokeWidth: 0,
        fillColor: "transparent"
      },
      label: {
        text,
        borderColor: "transparent",
        borderWidth: 0,
        textAnchor: "middle",
        offsetY,
        style: {
          background: "transparent",
          color,
          fontSize: "12px",
          fontWeight: 700,
          cssClass: "monthly-contracts-chart__clip-annotation monthly-contracts-chart__flow-value-annotation"
        }
      }
    }
  }

  activeAxisScale(values) {
    const nums = (values || []).filter((value) => value != null && !Number.isNaN(value))
    if (!nums.length) return { min: 0, max: 1 }

    const minValue = Math.min(...nums)
    const maxValue = Math.max(...nums)
    const span = Math.max(maxValue - minValue, maxValue * 0.08, 1)
    const paddedMin = minValue - span * 0.06
    const paddedMax = maxValue + span * 0.18

    const axisRange = (paddedMax - paddedMin) / (LINE_BAND_END - LINE_BAND_START)
    const min = paddedMin - axisRange * LINE_BAND_START
    const max = min + axisRange

    return { min, max }
  }

  buildYAxis(scales) {
    const activeScale = scales.active
    const barScale = scales.bars

    return [
      {
        seriesName: this.stockLabel(),
        show: true,
        opposite: true,
        floating: true,
        min: activeScale.min,
        max: activeScale.max,
        forceNiceScale: false,
        tickAmount: 4,
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      {
        show: true,
        opposite: false,
        min: barScale.min,
        max: barScale.max,
        forceNiceScale: false,
        tickAmount: 4,
        labels: {
          show: false
        },
        axisBorder: { show: false },
        axisTicks: { show: false }
      }
    ]
  }

  buildView() {
    const range = this.selectedRangeValue
    const months = this.collectMonths()
    const today = new Date()

    if (/^\d{4}$/.test(range)) {
      const calendarMonths = monthRangeForYear(range, today)
      const filled = fillMultiFlowMonthBuckets(calendarMonths, months)
      return this.timelineView(filled, { shortYear: false, granularity: "month" })
    }

    if (range === "12m") {
      const calendarMonths = monthRangeEndingAt(today, 12)
      const filled = fillMultiFlowMonthBuckets(calendarMonths, months)
      return this.timelineView(filled, { shortYear: true, granularity: "month" })
    }

    if (range === "all") {
      const calendarMonths = monthRangeFromFirstDataToToday(months, today)
      const filled = fillMultiFlowMonthBuckets(calendarMonths, months)
      const granularity = this.resolveTimelineGranularity(filled)
      const points = granularity === "month" ? filled : this.aggregateMonths(filled, granularity)
      return this.timelineView(points, { shortYear: true, granularity })
    }

    return this.timelineView(months, { shortYear: true, granularity: "month" })
  }

  resolveTimelineGranularity(months) {
    const monthCount = months.length
    if (monthCount <= TIMELINE_MONTHLY_MAX) return "month"

    const yearCount = new Set(months.map((point) => point.year)).size
    const options = [
      { id: "month", count: monthCount },
      { id: "semester", count: Math.ceil(monthCount / 6) },
      { id: "quarter", count: Math.ceil(monthCount / 3) },
      { id: "year", count: yearCount }
    ]

    const viable = options.filter((option) => option.id !== "month" || option.count <= TIMELINE_TARGET_MAX)
    const inRange = viable.filter(
      (option) => option.count >= TIMELINE_TARGET_MIN && option.count <= TIMELINE_TARGET_MAX
    )

    if (inRange.length) {
      return inRange.reduce((best, option) => (option.count > best.count ? option : best)).id
    }

    return viable.reduce((best, option) => {
      const distance = this.timelineBucketDistance(option.count)
      const bestDistance = this.timelineBucketDistance(best.count)
      return distance < bestDistance ? option : best
    }).id
  }

  timelineBucketDistance(count) {
    if (count >= TIMELINE_TARGET_MIN && count <= TIMELINE_TARGET_MAX) return 0
    if (count < TIMELINE_TARGET_MIN) return TIMELINE_TARGET_MIN - count
    return count - TIMELINE_TARGET_MAX
  }

  aggregateMonths(months, granularity) {
    const buckets = []
    const bucketIndex = new Map()

    months.forEach((point) => {
      const key = this.timelineBucketKey(point, granularity)
      let bucket = bucketIndex.get(key)

      if (!bucket) {
        bucket = {
          year: Number(point.year),
          monthIndex: point.monthIndex,
          quarter: granularity === "quarter" ? Math.floor(point.monthIndex / 3) + 1 : null,
          semester: granularity === "semester" ? (point.monthIndex < 6 ? 1 : 2) : null,
          subscriptions: null,
          terminations: null,
          imports: null,
          active: null,
          periodLabel: this.formatPeriodLabel(point, granularity)
        }
        bucketIndex.set(key, bucket)
        buckets.push(bucket)
      }

      bucket.subscriptions = this.sumFlowValues(bucket.subscriptions, point.subscriptions)
      bucket.terminations = this.sumFlowValues(bucket.terminations, point.terminations)
      bucket.imports = this.sumFlowValues(bucket.imports, point.imports)

      if (point.active != null && !Number.isNaN(point.active)) {
        bucket.active = point.active
        bucket.monthIndex = point.monthIndex
      }
    })

    return buckets
  }

  timelineBucketKey(point, granularity) {
    if (granularity === "year") return String(point.year)

    if (granularity === "semester") {
      const semester = point.monthIndex < 6 ? 1 : 2
      return `${point.year}-S${semester}`
    }

    const quarter = Math.floor(point.monthIndex / 3) + 1
    return `${point.year}-Q${quarter}`
  }

  formatPeriodLabel(point, granularity, shortYear = true) {
    if (granularity === "year") return String(point.year)

    const yearLabel = shortYear ? String(point.year).slice(-2) : String(point.year)

    if (granularity === "semester") {
      const semester = point.monthIndex < 6 ? 1 : 2
      return `S${semester} ${yearLabel}`
    }

    const quarter = Math.floor(point.monthIndex / 3) + 1
    return `T${quarter} ${yearLabel}`
  }

  sumFlowValues(current, value) {
    if (value == null || Number.isNaN(value)) return current
    return (current || 0) + Number(value)
  }

  collectMonths() {
    const payload = this.yearsValue.years || {}
    const years = Object.keys(payload).sort()
    const months = []

    years.forEach((year) => {
      const series = payload[year] || {}
      for (let index = 0; index < 12; index += 1) {
        const point = {
          year,
          monthIndex: index,
          subscriptions: this.pointValue(series.subscriptions, index),
          terminations: this.pointValue(series.terminations, index),
          imports: this.pointValue(series.imports, index),
          active: this.pointValue(series.active, index)
        }

        if (this.monthHasData(point)) months.push(point)
      }
    })

    return months
  }

  yearView(year) {
    const payload = this.yearsValue.years || {}
    const series = payload[year] || {}
    const subscriptions = this.normalizeSeries(series.subscriptions)
    const terminations = this.normalizeSeries(series.terminations)
    const active = this.normalizeSeries(series.active)
    const importsRaw = this.normalizeSeries(series.imports)
    const { start, end } = this.yearViewMonthBounds(
      year,
      subscriptions,
      terminations,
      active,
      importsRaw
    )
    const sliceSeries = (values) => values.slice(start, end + 1)

    const rawSeries = this.toChartSeries({
      subscriptions: sliceSeries(subscriptions),
      terminations: sliceSeries(terminations),
      imports: sliceSeries(importsRaw),
      active: sliceSeries(active)
    })

    return {
      categories: MONTH_SHORT_FR.slice(start, end + 1),
      monthMeta: Array.from({ length: end - start + 1 }, (_, index) => ({
        year: Number(year),
        monthIndex: start + index
      })),
      ...this.applyBarCaps(rawSeries)
    }
  }

  timelineView(months, { shortYear, granularity = "month" } = {}) {
    const rawSeries = this.toChartSeries({
      subscriptions: months.map((point) => point.subscriptions),
      terminations: months.map((point) => point.terminations),
      imports: months.map((point) => point.imports),
      active: months.map((point) => point.active)
    })

    return {
      categories: months.map((point) => point.periodLabel || this.formatMonthLabel(point, shortYear)),
      monthMeta: months.map((point) => ({
        year: Number(point.year),
        monthIndex: point.monthIndex,
        quarter: point.quarter || null,
        semester: point.semester || null,
        periodLabel: point.periodLabel || null,
        granularity
      })),
      granularity,
      ...this.applyBarCaps(rawSeries)
    }
  }

  toChartSeries({ subscriptions, terminations, imports, active }) {
    return [
      { name: this.stockLabel(), type: "area", data: active },
      { name: this.flowLabel("subscriptions", "Souscriptions"), type: "column", data: subscriptions },
      { name: this.flowLabel("imports", "Imports"), type: "column", data: imports },
      {
        name: this.flowLabel("terminations", "Résiliations"),
        type: "column",
        data: (terminations || []).map((value) => {
          if (value == null || Number.isNaN(value)) return null
          return -Math.abs(Number(value))
        })
      }
    ]
  }

  monthHasData(point) {
    return [point.subscriptions, point.terminations, point.imports, point.active]
      .some((value) => value != null && !Number.isNaN(value))
  }

  monthSeriesBounds(...seriesArrays) {
    let start = 0
    let end = 11

    while (start <= 11 && !this.monthIndexHasData(seriesArrays, start)) start += 1
    while (end >= start && !this.monthIndexHasData(seriesArrays, end)) end -= 1

    if (start > end) return { start: 0, end: 11 }

    return { start, end }
  }

  yearViewMonthBounds(year, ...seriesArrays) {
    const yearNum = Number(year)
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonthIndex = now.getMonth()

    if (yearNum === currentYear) {
      return { start: 0, end: currentMonthIndex }
    }

    if (yearNum < currentYear) {
      return { start: 0, end: 11 }
    }

    return this.monthSeriesBounds(...seriesArrays)
  }

  monthIndexHasData(seriesArrays, index) {
    return seriesArrays.some((values) => {
      const value = values?.[index]
      return value != null && !Number.isNaN(value)
    })
  }

  pointValue(values, index) {
    if (!Array.isArray(values) || values[index] === undefined || values[index] === "") return null
    if (values[index] === null) return null
    const number = Number(values[index])
    return Number.isFinite(number) ? number : null
  }

  formatMonthLabel(point, shortYear = false) {
    const label = MONTH_SHORT_FR[point.monthIndex] || String(point.monthIndex + 1)
    if (!shortYear) return label
    return `${label} ${String(point.year).slice(-2)}`
  }

  normalizeSeries(values) {
    if (!Array.isArray(values)) return Array(12).fill(null)
    return values.map((value) => {
      if (value === null || value === undefined || value === "") return null
      const number = Number(value)
      return Number.isFinite(number) ? number : null
    })
  }
}
